import { useCallback, useEffect, useState } from 'react'
import moment from 'moment'
import { useHistory, useLocation, useParams } from 'react-router-dom'

import { ROUTES } from 'config/routes'
import { useSnackbar } from 'components/Snackbar/useSnackbar'
import { deliveryReportStorage } from 'repositories/DeliveryReportStorage'
import { getDeliveryReportTenant } from 'repositories/ShipmentApi/getDeliveryReportTenant'
import { formatDeliveryReportTenantName } from 'repositories/ShipmentApi/getDeliveryReportTenant/formatTenantName'
import { searchShipments } from 'repositories/ShipmentApi/searchShipments'
import { getTruckbookApiErrorMessage } from 'repositories/getTruckbookApiErrorMessage'
import { DELIVERY_WORK_QUERY_KEY, DELIVERY_WORK_ACTION } from 'constants/deliveryWorkQueryParams'

import type { ShipmentListItem, TabStatus } from '../type'

const SHIPMENT_STATUS_DELIVERING = 1 as const
const SHIPMENT_STATUS_COMPLETED = 2 as const

const formatDate = (value: string | null): string | null =>
  value ? moment(value).format('YYYY/MM/DD HH:mm:ss') : null

const toShipmentListItem = (s: {
  shipmentUid: string
  createdAt: string | null
  completedAt: string | null
  shipmentVisits: Array<{ deliveryDestination: string | null }>
}): ShipmentListItem => ({
  uid: s.shipmentUid,
  registeredAt: formatDate(s.createdAt) ?? '',
  completedAt: formatDate(s.completedAt),
  // 複数visitがある場合も一覧では先頭のみ表示
  deliveryDestinationName: s.shipmentVisits[0]?.deliveryDestination ?? null,
})

export const useSpDeliveryListController = () => {
  const { deliveryReportUid } = useParams<{ deliveryReportUid: string }>()
  const history = useHistory()
  const { pathname, search } = useLocation()
  const { snackbar, notify } = useSnackbar()

  const statusParam = new URLSearchParams(search).get('status')
  const activeTab: TabStatus = statusParam === 'completed' ? 'completed' : 'active'

  const [tenantName, setTenantName] = useState('')
  const [activeShipments, setActiveShipments] = useState<ShipmentListItem[]>([])
  const [completedShipments, setCompletedShipments] = useState<ShipmentListItem[]>([])
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    const load = async () => {
      const driverPhone = deliveryReportStorage.getDriverPhone()
      // 電話番号未入力の場合はスキップ（通常は work 画面で保存済みのはず）
      if (!driverPhone) return

      const shipmentUids = deliveryReportStorage.getShipmentUids()
      // shipmentUids が空の場合は「この端末で登録した shipment なし」として空表示
      // API を呼ばずに抜けることで BE の shipment_uids: [] の解釈（全件返却 or 空返却）に
      // 依存しない実装にする
      if (shipmentUids.length === 0) return

      setIsLoading(true)
      try {
        const [tenantRes, activeRes, completedRes] = await Promise.all([
          getDeliveryReportTenant(deliveryReportUid),
          searchShipments({
            deliveryReportUid,
            driverPhone,
            shipmentUids,
            status: SHIPMENT_STATUS_DELIVERING,
          }),
          searchShipments({
            deliveryReportUid,
            driverPhone,
            shipmentUids,
            status: SHIPMENT_STATUS_COMPLETED,
          }),
        ])
        setTenantName(formatDeliveryReportTenantName(tenantRes.data))
        setActiveShipments(
          [...activeRes.data.shipments]
            .sort((a, b) => (b.createdAt ?? '').localeCompare(a.createdAt ?? ''))
            .map(toShipmentListItem)
        )
        setCompletedShipments(
          [...completedRes.data.shipments]
            .sort((a, b) => (b.completedAt ?? '').localeCompare(a.completedAt ?? ''))
            .map(toShipmentListItem)
        )
      } catch (error) {
        notify({ message: getTruckbookApiErrorMessage(error), variant: 'error' })
      } finally {
        setIsLoading(false)
      }
    }

    load()
  }, [deliveryReportUid, notify])

  const handleTabChange = useCallback(
    (tab: TabStatus) => {
      history.replace(`${pathname}?status=${tab}`)
    },
    [history, pathname]
  )

  const handleShipmentClick = useCallback(
    (shipmentUid: string) => {
      const base = ROUTES.spDeliveryReportWork.replace(':deliveryReportUid', deliveryReportUid)
      history.push(`${base}?shipmentUid=${shipmentUid}`)
    },
    [history, deliveryReportUid]
  )

  const handleNewDelivery = useCallback(() => {
    const phone = deliveryReportStorage.getDriverPhone()
    const startQuery = `?${DELIVERY_WORK_QUERY_KEY}=${DELIVERY_WORK_ACTION.START_SHIPMENT}`
    if (phone) {
      history.push(
        `${ROUTES.spDeliveryReportWork.replace(':deliveryReportUid', deliveryReportUid)}${startQuery}`
      )
    } else {
      history.push(`/sp/delivery-report/${deliveryReportUid}${startQuery}`)
    }
  }, [history, deliveryReportUid])

  return {
    snackbar,
    tenantName,
    activeTab,
    shipments: activeTab === 'completed' ? completedShipments : activeShipments,
    isLoading,
    deliveryReportUid,
    handleTabChange,
    handleShipmentClick,
    handleNewDelivery,
  }
}
